// Community Analytics — connector-outreach engagement (ranked per brand, with a
// platform lens + trend chart) and messaging-inbox stats (per-channel volume,
// open/closed, median first-response). Mirrors Post/Email Analytics.
import React, { useEffect, useState, useCallback } from "react";
import { get } from "api/index";
import OperationsReport from "./OperationsReport";
import SkoolProspectAnalytics from "./SkoolProspectAnalytics";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

// Rank → green(120°, top) … red(0°, last), same treatment as Post/Email cards.
function rankHue(rank, total) {
  if (total <= 1) return 120;
  return Math.round(120 * (1 - rank / (total - 1)));
}

function BrandRankCard({ brand, rank, total }) {
  const hue = rankHue(rank, total);
  return (
    <div
      data-testid="community-brand-rank-card"
      style={{
        flex: "0 0 auto", minWidth: 210, borderRadius: 8, padding: "14px 16px",
        background: `hsl(${hue}, 75%, 92%)`, border: `1px solid hsl(${hue}, 60%, 70%)`,
        borderLeft: `4px solid hsl(${hue}, 65%, 42%)`,
      }}
    >
      <div style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>#{rank + 1}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.15, wordBreak: "break-word" }}>
        {brand.name || "(unknown brand)"}
      </div>
      <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
        {brand.sent} sent · {brand.replies} repl{brand.replies === 1 ? "y" : "ies"} ({brand.replyRate}%)
      </div>
      <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
        {brand.invites} inv · {brand.messages} dm · {brand.comments} cmt
        {brand.conversions ? ` · ${brand.conversions} conv` : ""}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div style={{ flex: "1 1 140px", minWidth: 140, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: "#94a3b8" }}>{sub}</div> : null}
    </div>
  );
}

const DAYS_OPTIONS = [7, 30, 90];

const CommunityAnalytics = () => {
  const [days, setDays] = useState(30);
  const [rankPlatform, setRankPlatform] = useState("all");
  const [rankPlatforms, setRankPlatforms] = useState([]);
  const [brands, setBrands] = useState([]);
  const [series, setSeries] = useState([]);
  const [inbox, setInbox] = useState({ channels: [], totals: {} });
  // Starts TRUE: the first render then already shows "Loading…" instead of
  // mounting the gated sections only to swap them out a moment later.
  const [loading, setLoading] = useState(true);
  const [rankLoading, setRankLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [br, rp, hist, ib] = await Promise.all([
        get(`/community-analytics/brands?days=${days}`),
        get(`/community-analytics/ranked-platforms`),
        get(`/community-analytics/outreach/history?days=${days}`),
        get(`/community-analytics/inbox?days=${days}`),
      ]);
      setBrands(br?.brands || []);
      setRankPlatforms(rp?.platforms || []);
      setRankPlatform("all");
      setSeries(hist?.series || []);
      setInbox({ channels: ib?.channels || [], totals: ib?.totals || {} });
    } catch (e) {
      setError(e?.message || e?.error || "Failed to load community analytics");
    } finally {
      setLoading(false);
    }
  }, [days]);

  const loadRanking = useCallback(async (platform) => {
    setRankLoading(true);
    try {
      const q = platform && platform !== "all" ? `&platform=${encodeURIComponent(platform)}` : "";
      const [br, hist] = await Promise.all([
        get(`/community-analytics/brands?days=${days}${q}`),
        get(`/community-analytics/outreach/history?days=${days}${q}`),
      ]);
      setBrands(br?.brands || []);
      setSeries(hist?.series || []);
    } catch (e) {
      setBrands([]);
    } finally {
      setRankLoading(false);
    }
  }, [days]);

  useEffect(() => { loadAll(); }, [loadAll]);
  const didMount = React.useRef(false);
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return; }
    loadRanking(rankPlatform);
  }, [rankPlatform, loadRanking]);

  const t = inbox.totals || {};

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: 8 }}>
        <h5 className="m-0" style={{ fontWeight: 700 }}>Community Analytics</h5>
        <div className="btn-group btn-group-sm" role="group" aria-label="Window">
          {DAYS_OPTIONS.map((d) => (
            <button key={d} type="button"
              className={`btn btn-sm ${days === d ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setDays(d)}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {loading ? <p className="text-muted">Loading…</p> : (
        <>
          {/* ── Outreach ─────────────────────────────────────────── */}
          <div className="card border-0 mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: 8 }}>
                <p className="m-0" style={{ fontSize: 14, fontWeight: 600 }}>
                  Top brands by outreach engagement
                  {rankPlatform !== "all" && <span style={{ fontWeight: 500, color: "#64748b" }}> · {rankPlatform}</span>}
                </p>
                <div className="btn-group btn-group-sm" role="group" aria-label="Platform">
                  <button type="button"
                    className={`btn btn-sm ${rankPlatform === "all" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setRankPlatform("all")} disabled={rankLoading}>Overall</button>
                  {rankPlatforms.map((rp) => (
                    <button key={rp.platform} type="button"
                      className={`btn btn-sm ${rankPlatform === rp.platform ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setRankPlatform(rp.platform)} disabled={rankLoading}
                      title={`${rp.sent} sent`} style={{ textTransform: "capitalize" }}>{rp.platform}</button>
                  ))}
                </div>
              </div>
              {rankLoading ? <p className="text-muted m-0">Loading {rankPlatform} ranking…</p>
                : brands.length === 0 ? <p className="text-muted m-0">No outreach in this window.</p>
                : (
                  <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4 }}>
                    {brands.map((b, i) => <BrandRankCard key={b.brandId || i} brand={b} rank={i} total={brands.length} />)}
                  </div>
                )}
            </div>
          </div>

          <div className="card border-0 mb-3">
            <div className="card-body">
              <p className="m-0 mb-2" style={{ fontSize: 14, fontWeight: 600 }}>Outreach sent vs replies over time</p>
              <div style={{ width: "100%", height: 240 }}>
                {series.length === 0 ? <p className="text-muted">No data.</p> : (
                  <ResponsiveContainer>
                    <LineChart data={series} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="sent" stroke="#2563eb" strokeWidth={2} dot={false} name="Sent" />
                      <Line type="monotone" dataKey="replies" stroke="#16a34a" strokeWidth={2} dot={false} name="Replies" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* ── Inbox ────────────────────────────────────────────── */}
          <div className="card border-0 mb-3">
            <div className="card-body">
              <p className="m-0 mb-3" style={{ fontSize: 14, fontWeight: 600 }}>Inbox</p>
              <div className="d-flex flex-wrap mb-3" style={{ gap: 10 }}>
                <Kpi label="Conversations" value={t.conversations ?? 0} sub={`${t.open ?? 0} open · ${t.closed ?? 0} closed`} />
                <Kpi label="Inbound" value={t.inbound ?? 0} sub={`last ${days}d`} />
                <Kpi label="Outbound" value={t.outbound ?? 0} sub={`last ${days}d`} />
                <Kpi label="Median first response"
                  value={t.medianFirstResponseMin == null ? "—" : `${t.medianFirstResponseMin}m`}
                  sub="in → first reply" />
              </div>
              {(inbox.channels || []).length === 0 ? <p className="text-muted m-0">No inbox activity.</p> : (
                <div style={{ overflowX: "auto" }}>
                  <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: "#6b7280" }}>
                        <th>Channel</th>
                        <th className="text-right">Conversations</th>
                        <th className="text-right">Inbound</th>
                        <th className="text-right">Outbound</th>
                        <th className="text-right">Open</th>
                        <th className="text-right">Closed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inbox.channels.map((c) => (
                        <tr key={c.channel}>
                          <td style={{ textTransform: "capitalize", fontWeight: 600 }}>{c.channel}</td>
                          <td className="text-right">{c.conversations}</td>
                          <td className="text-right">{c.inbound}</td>
                          <td className="text-right">{c.outbound}</td>
                          <td className="text-right">{c.open}</td>
                          <td className="text-right">{c.closed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

        </>
      )}

      {/* OUTSIDE the loading gate on purpose. Neither section uses loadAll's
          data, and inside the gate they were unmounted when loading flipped
          true and mounted again when it flipped back — so each fetched twice
          per visit (on top of the route remount fixed in App.jsx). */}
      {/* Where every Skool person stands — the funnel the classifier keeps. */}
      <SkoolProspectAnalytics days={days} />

      {/* The full ledger: every op attempted on every platform, Skool
          included, with the per-day / per-platform / per-brand charts and
          the brand → platform → day → message cascade. The sections above
          count outreach that LANDED; this one counts everything tried. */}
      <OperationsReport days={days} />
    </div>
  );
};

export default CommunityAnalytics;
